import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  TextField,
  MenuItem,
  Autocomplete,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import countryList from "react-select-country-list";
import getSymbolFromCurrency from "currency-symbol-map";
import CustomPagination from "../../../components/CustomPagination";
import {
  sortedAllowanceEntries,
  formatAllowanceGrant,
  topupStatusOptions,
  topupStatusMeta,
  TOPUP_REVENUE_STATUSES,
} from "../../../constants/planConstants";
import { formatPrice } from "../../../utils/planHelpers";
import { formatDate } from "../../../utils/DateFormat";
import styles from "./style";

const COUNTRY_OPTIONS = countryList().getData().map((c) => c.label);

const TABLE_HEADERS = [
  "Topup Pack",
  "Allowances",
  "Qty",
  "Amount Paid",
  "Purchased",
  "Expires",
  "Status",
];

const renderAllowances = (purchase, styles) => {
  const entries = sortedAllowanceEntries(purchase?.allowances);
  if (entries.length === 0) return "—";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {entries.map(([key, value]) => {
        const { label, display } = formatAllowanceGrant(key, value);
        return (
          <Typography key={key} sx={{ fontSize: "13px", color: styles.tableCell.color }}>
            {label}: {display}
          </Typography>
        );
      })}
    </Box>
  );
};

// Amount and currency are rendered exactly as the backend supplied them — no
// conversion, no re-rounding beyond the shared display helper.
const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined || amount === "") return "—";
  const code = String(currency || "").toUpperCase();
  const symbol = code ? getSymbolFromCurrency(code) : "";
  const value = formatPrice(amount);
  if (symbol) return `${symbol}${value}`;
  return code ? `${code} ${value}` : value;
};

const TopupSalesPage = ({
  purchases = [],
  pagination = {},
  totalRevenue = null,
  packs = [],
  filters = {},
  loading = false,
  loadError = "",
  page = 1,
  rowsPerPage = 20,
  setPage,
  onRowsPerPageChange,
  onFilterChange,
  onResetFilters,
  onBack,
}) => {
  const packOptions = packs.map((pack) => ({
    id: pack?._id || pack?.id,
    name: pack?.name || "Untitled pack",
  }));

  // The contract returns a single totalRevenue number with no currency. It is
  // only safe to attach a symbol when every row on the page shares one
  // currency; otherwise the figure is shown unlabelled rather than guessing.
  const revenueCurrency = useMemo(() => {
    const codes = new Set(
      purchases
        .map((p) => String(p?.currency || "").toUpperCase())
        .filter(Boolean)
    );
    return codes.size === 1 ? Array.from(codes)[0] : "";
  }, [purchases]);

  const hasActiveFilters = Boolean(
    filters.packId ||
      filters.country ||
      filters.status ||
      filters.startDate ||
      filters.endDate
  );

  const revenueStatusHint = `Counts ${TOPUP_REVENUE_STATUSES.join(" and ")} purchases matching the current filters.`;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Box>
          <Typography sx={styles.title}>Topup Sales</Typography>
          <Typography sx={styles.subtitle}>
            Every topup purchased, newest first.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={onBack} sx={styles.backButton}>
          Back to Topup Management
        </Button>
      </Box>

      <Box sx={styles.summaryCard}>
        <Typography sx={styles.summaryLabel}>Total revenue</Typography>
        <Typography sx={styles.summaryValue} data-testid="topup-total-revenue">
          {totalRevenue === null || totalRevenue === undefined
            ? "—"
            : formatAmount(totalRevenue, revenueCurrency)}
        </Typography>
        <Typography sx={styles.summaryHint}>{revenueStatusHint}</Typography>
        {!revenueCurrency && purchases.length > 0 && (
          <Typography sx={styles.summaryHint}>
            Purchases on this page span multiple currencies, so no single
            currency is shown.
          </Typography>
        )}
      </Box>

      <Box sx={styles.filterCard}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Pack"
              value={filters.packId || ""}
              onChange={(e) => onFilterChange("packId", e.target.value)}
            >
              <MenuItem value="">All packs</MenuItem>
              {packOptions.map((pack) => (
                <MenuItem key={pack.id} value={pack.id}>
                  {pack.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={COUNTRY_OPTIONS}
              value={filters.country || null}
              onChange={(e, value) => onFilterChange("country", value || "")}
              renderInput={(params) => (
                <TextField {...params} label="Country" placeholder="All countries" />
              )}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filters.status || ""}
              onChange={(e) => onFilterChange("status", e.target.value)}
            >
              <MenuItem value="">All statuses</MenuItem>
              {topupStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="From"
                value={filters.startDate || null}
                onChange={(value) => onFilterChange("startDate", value)}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                  field: { clearable: true },
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="To"
                value={filters.endDate || null}
                minDate={filters.startDate || undefined}
                onChange={(value) => onFilterChange("endDate", value)}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                  field: { clearable: true },
                }}
              />
            </Grid>
          </LocalizationProvider>

          {hasActiveFilters && (
            <Grid item xs={12}>
              <Button size="small" onClick={onResetFilters} sx={styles.resetButton}>
                Clear filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>

      {loadError && (
        <Alert severity="error" sx={styles.errorAlert}>
          {loadError}
        </Alert>
      )}

      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <TableCell key={header} sx={styles.tableHeaderCell}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center">
                  <Box sx={styles.loadingBox}>
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : purchases.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={TABLE_HEADERS.length}
                  align="center"
                  sx={styles.emptyCell}
                >
                  {/* An API failure is reported by the Alert above; this row
                      only ever describes a genuinely empty result. */}
                  <Typography>
                    {loadError
                      ? "Topup sales could not be loaded."
                      : hasActiveFilters
                        ? "No topup purchases match these filters."
                        : "No topup purchases yet."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              purchases.map((purchase, index) => {
                const status = topupStatusMeta(purchase?.status);
                return (
                  <TableRow key={purchase?._id || purchase?.id || index}>
                    <TableCell sx={styles.tableCell}>
                      {purchase?.packNameSnapshot || "—"}
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      {renderAllowances(purchase, styles)}
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      {purchase?.quantity ?? "—"}
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      {formatAmount(purchase?.amountPaid, purchase?.currency)}
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      {formatDate(purchase?.purchasedAt)}
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      {formatDate(purchase?.expiresAt)}
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                        sx={styles.statusChip}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomPagination
        pageCount={pagination?.total || 0}
        page={page}
        setPage={setPage}
        setRowsPerPage={onRowsPerPageChange}
        rowsPerPage={rowsPerPage}
        totalItems={pagination?.total || 0}
      />
    </Box>
  );
};

export default TopupSalesPage;
